<template>
  <form
    :class="bemBlock"
    :action="url"
    novalidate
    @submit.prevent.stop="handleSubmit"
  >
    <BaseFieldText
      :class-label="'sr-only'"
      :label="$msg('field.city.zip.code', 'storeLocator')"
      :name="'postalCode'"
      :placeholder="$msg('field.city.zip.code', 'storeLocator')"
      :required="true"
      :data-missing-error="$msg('error.message.required', 'forms', null)"
    />
    <div class="form__actions">
      <button
        type="button"
        :class="[
          bemElement('detect-location'),
          'u-btn-clean',
          'link',
          'link--highlight'
        ]"
        :data-action="url"
        :disabled="!isGeolocationActive"
        @click="handleGeolocation"
      >
        {{ $msg('button.use.my.location', 'storeLocator') }}
      </button>
      <button
        :class="[bemElement('submit'), 'btn btn--primary']"
        type="submit"
      >
        {{ $msg('button.find.stores', 'storeLocator') }}
      </button>
    </div>
    <p
      v-if="formErrorMessage !== ''"
      :class="bemElement('error-message')"
    >
      {{ formErrorMessage }}
    </p>
  </form>
</template>

<script>
import { urlWithSearchParams } from '../../common/utils/url';
import bemClassesMixin from '../mixins/bemClassesMixin';
import BaseFieldText from './forms/BaseFieldText.vue';
import getFrontConfig from '../../common/getFrontConfig';
import { getDefaultCustomCoordinates, getDefaultCustomSearchRadius, parseCountryCode } from '../../common/utils/countriesUtils';
import map from '../../storeLocator/map';
import { validFormFields, resetFormFieldsValidation } from '../../storeLocator/index';

const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'BaseStoreLocatorForm',

  components: {
    BaseFieldText,
  },

  mixins: [bemClassesMixin],

  props: {
    url: {
      type: String,
      default: null,
      required: false,
    },
  },

  data() {
    return {
      formErrorMessage: '',
      geolocation: null,
    };
  },

  computed: {
    isGeolocationActive() {
      return !!this.geolocation;
    },
    mapApiKey() {
      return FRONT_CONFIG.mapApiKey;
    },
  },

  watch: {},

  mounted() {
    this.$nextTick(() => {
      if (window.navigator.geolocation) {
        window.navigator.geolocation.getCurrentPosition(
          (position) => { this.geolocation = position; },
        );
      }
    });
  },

  methods: {
    handleGeolocation() {
      resetFormFieldsValidation(`.${this.bemBlock[0]}`);
      // eslint-disable-next-line prefer-destructuring
      const radius = document.querySelector('.results')?.dataset?.radius;
      const params = {
        radius,
        lat: this.geolocation.coords.latitude,
        long: this.geolocation.coords.longitude,
      };
      this.searchStores(params);
    },
    async handleSubmit(event) {
      const { value } = event.target.elements.postalCode;
      const { countryCode, mapApiKey } = FRONT_CONFIG;
      const customCountryCoordinates = getDefaultCustomCoordinates(countryCode);
      const radius = getDefaultCustomSearchRadius(countryCode) || 20000;
      let params;
      if (customCountryCoordinates && !value) {
        params = {
          lat: customCountryCoordinates?.lat,
          long: customCountryCoordinates?.lng,
          countryCode,
          radius,
        };
      } else {
        if (!validFormFields(`.${this.bemBlock[0]}`)) return;
        const country = parseCountryCode(countryCode);
        const location = await map.geocodeAddress(value, country, mapApiKey);
        if (location) {
          params = {
            lat: location.lat,
            long: location.lng,
            countryCode,
            ...(!value && { radius }),
          };
        }
      }

      if (params) {
        this.searchStores(params);
      }
    },
    searchStores(params) {
      this.formErrorMessage = '';
      const urlWithParams = urlWithSearchParams(this.url, params);

      fetch(decodeURIComponent(urlWithParams), {
        method: 'GET',
      })
        .then((response) => {
          try {
            return response.json();
          } catch (error) {
            window.console.warn('Status:', response.statusText);
            return error;
          }
        })
        .then((data) => {
          if (data.error) {
            window.console.warn('Message:', data.message);
          } else if (!data.stores?.length) {
            this.formErrorMessage = this.$msg('error.notAvailable', 'inStoreStock');
            this.$emit('store-locator:result', data);
          } else {
            this.$emit('store-locator:result', data);
          }
        })
        .catch(() => {
          this.formErrorMessage = this.$msg('error.general', 'inStoreStock');
        });
    },
  },
};
</script>
